import { Col, Row } from 'react-bootstrap';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useFetchAgencyChannelSettings from 'components/domain/channelSettings/useFetchAgencyChannelSettings';
import LeadChannelIcon from 'components/domain/lead/LeadChannelIcon';
import VrboCancellationPolicyField from 'components/domain/vrbo/VrboCancellationPolicyField';
import VrboCustomTiersField from 'components/domain/vrbo/VrboCustomTiersField';
import RadioGroupField from 'components/forms/radioGroupField/RadioGroupField';
import TextField from 'components/forms/textField/TextField';
import SelectField from 'components/forms/selectField/SelectField';
import useIsMobileScreen from 'hooks/useIsMobileScreen';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import { BookingType } from 'models/BookingType';
import { Channel, VrboCancellationPolicy } from 'models/Channels';
import { LeadChannel } from 'models/Leads';
import { PropertyBusinessType } from 'models/Properties';
import {
  FormSectionColumn,
  SectionTitleContainer,
} from 'pages/property/common/Common.styles';
import PropertyFormFieldLabel from 'pages/property/common/PropertyFormFieldLabel';
import TooltipContentWithLearnMore from 'pages/property/common/tooltip/TooltipContentWithLearnMore';
import { Breakpoint, breakpoints } from 'styles/mediaQuery';
import { PropertySettingsMainTabFormValues } from '../../PropertySettingsMainTab.schema';
import usePropertySettingsInitData from '../../../usePropertySettingsInitData';
import { PropertyVrboSettingsCollapseStyled } from './PropertyVrboSettings.styles';
import PropertyVrboSettingsInvoicePaymentMethodsLink from './PropertyVrboSettingsInvoicePaymentMethodsLink';
import PropertyVrboSettingsListedByField from './PropertyVrboSettingsListedByField';
import { VRBO_AVAILABLE_BOOKING_TYPES } from './PropertyVrboSettings.constants';
import PropertyVrboSettingsPromotionsField from './PropertyVrboSettingsPromotionsField';
import {
  findNewListingDiscountPromotion,
  isNewListingDiscountFieldDisabled,
} from './PropertyVrboNewListingDiscount.utils';

const PropertyVrboSettings = () => {
  const isMobileScreen = useIsMobileScreen(breakpoints[Breakpoint.SM]);
  const { getCopyAddonIcon } = useCopyToClipboard();
  const { channelSettings, isFetching: isLoadingChannelSettings } =
    useFetchAgencyChannelSettings();
  const { property } = usePropertySettingsInitData();
  const { t } = useTranslation();
  const { setValue, getValues } =
    useFormContext<PropertySettingsMainTabFormValues>();
  const {
    // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    vrboSettings: { propertyId: vrboPropertyId, promotions },
  } = useWatch<PropertySettingsMainTabFormValues>();
  const cancellationPolicy = useWatch<PropertySettingsMainTabFormValues>({
    name: 'vrboSettings.cancellationPolicy',
  });

  const isVrboActive = !!channelSettings?.find(
    ({ enumId }) => enumId === Channel.homeaway,
  )?.isActive;

  const isHotel = property?.businessType === PropertyBusinessType.HOTEL;
  if (!isVrboActive || isLoadingChannelSettings || isHotel) {
    return null;
  }

  const sectionTitle = (
    <SectionTitleContainer>
      <LeadChannelIcon channel={LeadChannel.VRBO} />
      {t('pageProperty.mainSettings.vrboSettings.sectionTitle')}
    </SectionTitleContainer>
  );

  const bookingTypeFieldName = 'vrboSettings.bookingType';
  const bookingTypeOptions = VRBO_AVAILABLE_BOOKING_TYPES.map(
    (type: BookingType) => ({
      value: type,
      label: t(`common.bookingType.${type}`),
    }),
  );

  const propertyLocationOptions = [true, false].map((value) => ({
    label: t(
      `pageProperty.mainSettings.vrboSettings.showPropertyLocation.option_${value}`,
    ),
    value: `${value}`,
  }));

  const newListingDiscountOptions = [
    { label: t('common.disabled'), value: 'false' },
    { label: t('common.enabled'), value: 'true' },
  ];

  const newListingDiscountPromotion =
    findNewListingDiscountPromotion(promotions);
  const isNewListingDiscountDisabled = isNewListingDiscountFieldDisabled(
    newListingDiscountPromotion,
  );

  return (
    <PropertyVrboSettingsCollapseStyled
      data-testid="vrbo-settings"
      defaultExpanded
      label={sectionTitle}
      variant="card"
    >
      <fieldset>
        <Row>
          <FormSectionColumn>
            <TextField
              label={t('pageProperty.mainSettings.vrboSettings.propertyId')}
              name="vrboSettings.propertyId"
              colSmInput={7}
              colSmLabel={5}
              disabled
              rightAddons={[getCopyAddonIcon(vrboPropertyId)]}
            />
            <SelectField
              label={
                <PropertyFormFieldLabel name={bookingTypeFieldName}>
                  {t('pageProperty.mainSettings.vrboSettings.bookingType')}
                </PropertyFormFieldLabel>
              }
              name={bookingTypeFieldName}
              colSmInput={7}
              colSmLabel={5}
              options={bookingTypeOptions}
              required
            />
            <VrboCancellationPolicyField
              name="vrboSettings.cancellationPolicy"
              label={
                <PropertyFormFieldLabel name="vrboSettings.cancellationPolicy">
                  {t(
                    'pageProperty.mainSettings.vrboSettings.cancellationPolicy.label',
                  )}
                </PropertyFormFieldLabel>
              }
              required
              onCustomSelected={() => {
                if (
                  !getValues('vrboSettings.customCancellationPolicies')?.length
                ) {
                  setValue('vrboSettings.customCancellationPolicies', [
                    {
                      id: crypto.randomUUID(),
                      refundPercentage: 0,
                      refundThreshold: 0,
                    },
                  ]);
                }
              }}
            />
            {isMobileScreen &&
              cancellationPolicy === VrboCancellationPolicy.CUSTOM && (
                <VrboCustomTiersField
                  name="vrboSettings.customCancellationPolicies"
                  getEmptyTier={() => ({
                    id: crypto.randomUUID(),
                    refundPercentage: 0,
                    refundThreshold: 0,
                  })}
                  getTierKey={(tier) => tier.id!}
                />
              )}
          </FormSectionColumn>
          <FormSectionColumn className="vrbo-settings-column-2">
            <RadioGroupField
              label={t(
                'pageProperty.mainSettings.vrboSettings.showPropertyLocation.label',
              )}
              name="vrboSettings.showPropertyExactLocation"
              options={propertyLocationOptions}
              colSmInput={7}
              colSmLabel={5}
            />
            <RadioGroupField
              disabled={isNewListingDiscountDisabled}
              horizontal
              label={t(
                'pageProperty.mainSettings.vrboSettings.newListingDiscount',
              )}
              name="vrboSettings.newListingDiscount"
              options={newListingDiscountOptions}
              colSmInput={7}
              colSmLabel={5}
              rightTooltip={
                <TooltipContentWithLearnMore
                  labelTooltipText={t(
                    'pageProperty.mainSettings.vrboSettings.newListingDiscountTooltip',
                  )}
                  learnMoreUrl="https://help.hostfully.com/en/articles/8694980-activate-vrbo-new-listing-discount"
                />
              }
            />
            <PropertyVrboSettingsListedByField />

            <PropertyVrboSettingsPromotionsField />
          </FormSectionColumn>
        </Row>
        {!isMobileScreen &&
          cancellationPolicy === VrboCancellationPolicy.CUSTOM && (
            <Row>
              <Col className="policy-periods-column" xs={12}>
                <VrboCustomTiersField
                  name="vrboSettings.customCancellationPolicies"
                  getEmptyTier={() => ({
                    id: crypto.randomUUID(),
                    refundPercentage: 0,
                    refundThreshold: 0,
                  })}
                  getTierKey={(tier) => tier.id!}
                />
              </Col>
            </Row>
          )}
        <Row>
          <Col xs={12}>
            <PropertyVrboSettingsInvoicePaymentMethodsLink />
          </Col>
        </Row>
      </fieldset>
    </PropertyVrboSettingsCollapseStyled>
  );
};

export default PropertyVrboSettings;
